/**
 * 入住修改
 */
import { MemberLevel, PriceChannel } from '@/config/constant';
import { CheckInEditorAction } from '@/redux/actions/reservation/constant';
import {
  RentTimeInfo,
  ConfigRoomPrice,
  EditRentRoomInfo,
  SourceChannelInfo,
  ChannelItem,
  StrategyItem,
  RemarkInfo,
} from './base.d';

// 公共信息
export interface CommonInfo {
  memberLevel: MemberLevel;
  ownerWYId: string; // 房主网鱼id
  isDirectChannel: boolean; // 是否为直连渠道
  auditPassedCount: number; // 已过夜审次数
}

// 订单原信息
interface OriginOrderInfo {
  resvNo: string; // 入住主单号
  renewalId: number; // 续住编号
  isLatestRecord: boolean; // 是否是最后一条续住记录
  expectedArrivalTime: number; // 预抵时间
  occDuration: number; // 入住时长
  expectedDepartureTime: number; // 预离时间
  sourceChannelId: number; // 渠道id
  sourceChannelName: string; // 渠道名称
  priceStrategyId: number; // 计价策略
  priceStrategyName: string; // 策略名称
  channelOrderNo: string; // 渠道单号
  priceChannel: PriceChannel; // 渠道代码
  remark: string; // 预订备注信息
  checkInRemark: string; // 入住备注信息
}

export interface MainState {
  originBaseInfo: OriginOrderInfo; // 订单原信息
  originCouponList: string[]; // 原始券列表
  originMarketPriceMap: { [key: string]: number }; // 原门市价
  originRoomPriceMap: Record<string, Record<string, string>>; // 原订单房价
  commonInfo: CommonInfo; // 基础信息
  rentTimeInfo: RentTimeInfo; // 预订日期
  channelInfo: SourceChannelInfo; // 客源渠道
  rentRoomInfos: EditRentRoomInfo[]; // 入住房信息
  remarkInfo: RemarkInfo;
}

export interface ReducerState extends MainState {
  isInit: boolean; // 初始化
  isLoading: boolean; // 加载中
  channelList: ChannelItem[]; // 渠道列表
  strategyList: StrategyItem[]; // 策略列表
  roomPriceList: ConfigRoomPrice[]; // 房价信息
}

const initialState: Partial<ReducerState> = {
  isInit: false,
  isLoading: true,
  commonInfo: {
    memberLevel: MemberLevel.Common,
    ownerWYId: null, // 默认为空
    isDirectChannel: true,
    auditPassedCount: 999,
  },
};

type ActionHandler<T> = (state: ReducerState, action: { payload: T }) => ReducerState;

const reducerMap = {
  [CheckInEditorAction.initDefaultState]: <ActionHandler<Partial<ReducerState>>>((
    state,
    { payload },
  ) => {
    return { ...state, ...payload };
  }),
  [CheckInEditorAction.setLoadingStatus]: <ActionHandler<boolean>>((state, { payload }) => {
    return { ...state, isLoading: payload };
  }),
  [CheckInEditorAction.setInitStatus]: <ActionHandler<boolean>>((state, { payload }) => {
    return { ...state, isInit: payload };
  }),
  [CheckInEditorAction.setCommonData]: <ActionHandler<CommonInfo>>((state, { payload }) => {
    const { commonInfo } = state;
    return { ...state, commonInfo: { ...commonInfo, ...payload } };
  }),
  [CheckInEditorAction.setRentTimeData]: <ActionHandler<RentTimeInfo>>((state, { payload }) => {
    return { ...state, rentTimeInfo: payload };
  }),
  [CheckInEditorAction.setChannelData]: <ActionHandler<Partial<SourceChannelInfo>>>((
    state,
    { payload },
  ) => {
    const { channelInfo } = state;
    return { ...state, channelInfo: { ...channelInfo, ...payload } };
  }),
  [CheckInEditorAction.setRentRoomList]: <ActionHandler<EditRentRoomInfo[]>>((
    state,
    { payload },
  ) => {
    return { ...state, rentRoomInfos: payload };
  }),
  [CheckInEditorAction.setRemarkInfo]: <ActionHandler<Partial<RemarkInfo>>>((
    state,
    { payload },
  ) => {
    const { remarkInfo } = state;
    return { ...state, remarkInfo: { ...remarkInfo, ...payload } };
  }),
  [CheckInEditorAction.setChannelList]: <ActionHandler<ChannelItem[]>>((state, { payload }) => {
    return { ...state, channelList: payload };
  }),
  [CheckInEditorAction.setStrategyList]: <ActionHandler<StrategyItem[]>>((state, { payload }) => {
    return { ...state, strategyList: payload };
  }),
  [CheckInEditorAction.setRoomPriceList]: <ActionHandler<ConfigRoomPrice[]>>((
    state,
    { payload },
  ) => {
    return { ...state, roomPriceList: payload };
  }),
};

export const reducerMapper = (state = initialState, action) => {
  const actionType = action.type;
  const reducer = reducerMap[actionType];
  if (typeof reducer === 'function') {
    return reducer(<ReducerState>state, action);
  }
  return state;
};

export const storeName = 'checkInEditor';
